/**
 * Trailing-edge throttle for high-frequency callbacks.
 *
 * Streamed tokens can arrive many times a second; calling back on every one
 * would re-render the message list per token. `trigger()` schedules `fn` to
 * run at most once per `intervalMs`; `flushNow()` runs it immediately if a
 * trigger is pending (call this when the stream ends, so the final content
 * never waits out the interval).
 */
export function createTrailingThrottle(intervalMs: number, fn: () => void) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending = false;

  function trigger(): void {
    pending = true;
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      if (pending) {
        pending = false;
        fn();
      }
    }, intervalMs);
  }

  function flushNow(): void {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (pending) {
      pending = false;
      fn();
    }
  }

  return { trigger, flushNow };
}
